import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  SafeAreaView,
  View,
  Text,
  TextInput,
  Platform,
  FlatList,
  StyleSheet,
  useWindowDimensions,
  ActivityIndicator,
} from "react-native";
import { useTranslation } from "react-i18next";
import CustomInput from "../../../components/CustomInput";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomButton from "../../../components/CustomButton";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import { useHRAdminEmployees } from "../../../hooks/useHRAdminEmployees";
import { showMessage } from "react-native-flash-message";
import Swal from 'sweetalert2';
import { Card } from "react-native-paper";

const SalaryReviewScreen = ({ selectedReview, selectedEmployee }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const common = createCommonStyles(colors);
  const { width, height } = useWindowDimensions();
  const styles = makeStyles(colors, width, height);
  const [rows, setRows] = useState([]);
  const ref = useRef(null);
  const [changes, setChanges] = useState({});
  const [originalRows, setOriginalRows] = useState([]);
  const [comments, setComments] = useState(selectedReview?.comments || "");
  const [isLoading, setIsLoading] = useState(false);
  const {
    getSalaryComponent,
    getEmplSalaryComponentCurrentReview,
    getEmplSalaryComponentPreviousReview,
    updateSalaryComponent,
  } = useHRAdminEmployees();

  function convertDateFormat(dateString) {
    if (!dateString) return null;
    const [yyyy, mm, dd] = dateString.split("T")[0].split("-");
    return `${dd}-${mm}-${yyyy}`;
  }

  useEffect(() => {
    if (
      !selectedReview?.emplSalaryId ||
      !selectedReview?.salarySchemeId ||
      !selectedEmployee?.employeeID
    )
      return;
    const fetchData = async () => {
      try {
        const [compsRes, currRes, prevRes] = await Promise.all([
          getSalaryComponent(
            selectedReview.emplSalaryId,
            selectedReview?.salarySchemeId
          ),
          getEmplSalaryComponentCurrentReview(selectedReview.emplSalaryId),
          getEmplSalaryComponentPreviousReview(
            selectedReview.salarySchemeId,
            selectedEmployee.employeeID,
            convertDateFormat(selectedReview.startDate)
          ),
        ]);
        const comps = compsRes.data ?? compsRes;
        const curr = currRes.data ?? currRes;
        const prev = prevRes.data ?? prevRes;

        const merged = comps.map((comp) => {
          const current = curr.find(
            (c) => c.salaryComponentId === comp.salarycomponentid
          );
          const previous = prev.find(
            (p) => p.salaryComponentId === comp.salarycomponentid
          );
          const c = current ? parseFloat(current.value) : null;
          const p = previous ? parseFloat(previous.value) : null;
          const d = (c ?? 0) - (p ?? 0);
          const pct = p && p !== 0 ? ((d / p) * 100).toFixed(2) : "-";
          return {
            id: comp.salarycomponentid,
            component: comp.fieldcaption,
            current: c !== null ? c.toFixed(2) : "",
            previous: p !== null ? p.toFixed(2) : "",
            diff: d.toFixed(2),
            diffPct: pct,
          };
        });
        console.log('merge' + JSON.stringify(comps))
        if (comps.length === 0)
          Swal.fire({
            title: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
              "common:nosalarycomponentsavailable"
            )}</text>`,
            icon: "warning",
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
              "common:cancel"
            )}</text>`,
            cancelButtonColor: "#d3d3d3",
            confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
              "common:confirmMessage"
            )}</text>`,
            confirmButtonColor: "#A0A2FF",
          })
        setRows(merged);
        setOriginalRows(merged);
        setChanges(merged);
      } catch (err) {
        console.error("SalaryReviewScreen fetch error:", err);
      }
    };
    if (selectedReview?.emplSalaryId && selectedEmployee?.employeeID) {
      fetchData();
    }
  }, [
    selectedReview.emplSalaryId,
    selectedReview.salarySchemeId,
    selectedEmployee?.employeeID,
    selectedReview.startDate,
  ]);


  useEffect(() => {
    if (originalRows && Array.isArray(originalRows)) {
      setRows(originalRows.map((row) => ({ ...row })));
      setChanges(originalRows.map((row) => ({ ...row })));
    }
    if (selectedReview) {
      setComments(selectedReview.comments || "");
    }
  }, [originalRows, selectedReview]);

  const handleEditPress = (index, value, field) => {
    const updatedChanges = [...changes];
    updatedChanges[index][field] = value;
    if (field === "current" || field === "previous") {
      const current = parseFloat(updatedChanges[index].current) || 0;
      const previous = parseFloat(updatedChanges[index].previous) || 0;
      const diff = current - previous;
      const diffPct = previous > 0 ? ((diff / previous) * 100).toFixed(2) : "-";
      updatedChanges[index].diff = diff.toFixed(2);
      updatedChanges[index].diffPct = diffPct;
    }
    setRows(updatedChanges);
  };

  const handleSave = async () => {
    let savePromises = [];
    let updateSucc = true;
    let insertSucc = true;

    try {
      const components = rows.map((row) => ({
        emplSalaryId: selectedReview?.emplSalaryId,
        salaryComponentId: row.id,
        value: row.current !== "" ? parseFloat(row.current) : 0,
        previous: row.previous !== "" ? parseFloat(row.previous) : 0,
        diff: parseFloat(row.diff),
        diffPct: parseFloat(row.diffPct),
      }));
      try {
        savePromises.push(
          updateSalaryComponent(components).catch((error) => {
            updateSucc = false;
          })
        );
        console.log("Sending components:", components);

        await Promise.all(savePromises);

        if (updateSucc) {
          console.log("changes:" + JSON.stringify(changes));
          setOriginalRows(changes);
          setRows(changes);
          showMessage({
            message: t("common:saveSuccess"),
            type: "success",
            duration: 3000,
          });
        } else {
          console.error("Update failed, response:", response);
          showMessage({
            message: t("common:saveError"),
            type: "danger",
            duration: 3000,
          });
        }
      } catch (error) {
        console.error("Save error:", error);
        showMessage({
          message: t("common:saveError"),
          type: "danger",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      showMessage({
        message: t("common:saveError"),
        type: "danger",
        duration: 3000,
      });
    }
  };

  const handleCancelChanges = () => {
    // Reset changes to their original state
    console.log("originalRows:", originalRows);
    console.log("selectedReview:", selectedReview);
    if (Array.isArray(originalRows)) {
      setChanges(originalRows.map((row) => ({ ...row })));
      setRows(originalRows.map((row) => ({ ...row })));
    } else {
      console.error("originalRows is undefined or not an array");
    }
    // Reset comments
    setComments(selectedReview?.comments || "");
  };

  const renderItem = useCallback(
    ({ item, index }) => (
      <Card style={common.cardstyle} key={item.id}>
        < Card.Content>
          <View style={styles.row}>
            <CustomInput
              value={item.component}
              editable={false}
              style={styles.cell}
              maxLen={50}
              type="REVIEWS"
            />
            <CustomInput
              value={item.current}
              editable={true}
              style={styles.cell}
              maxLen={50}
              type="REVIEWS"
              keyboardType="numeric"
              setValue={(e) =>
                handleEditPress(index, e.replace(/[^0-9.]/g, ""), "current")
              }
            />
            <CustomInput
              value={item.previous}
              editable={false}
              style={styles.cell}
              maxLen={50}
              type="REVIEWS"
              keyboardType="numeric"
              setValue={(e) =>
                handleEditPress(index, e.replace(/[^0-9.]/g, ""), "previous")
              }
            />
            <CustomInput
              value={item.diff}
              style={styles.cell}
              maxLen={50}
              type="REVIEWS"
              onChangeText={(e) => handleEditPress(index, item, "diff", e)}
            />
            <CustomInput
              value={item.diffPct}
              style={styles.cell}
              maxLen={50}
              type="REVIEWS"
              onChangeText={(e) => handleEditPress(index, item, "diffPct", e)}
            />
          </View>
        </Card.Content>
      </Card>
    ),
    [rows]
  );

  const flatListHeight = Platform.OS === "web" && height > 750 ? height * 0.38 : height * 0.25;

  return (
    <SafeAreaView style={styles.mainView}>
      <Text style={styles.title}>
        {selectedReview.reviewDescription || t("common:noDescription")} </Text>
      <TextInput
        style={[styles.input, { marginVertical: 5 }]}
        value={comments}
        placeholder={t("common:enterComments")}
        multiline />
      {/* </View> */}
      <View style={[styles.buttons]}>
        <CustomButton
          text={t("common:saveFunction")}
          onPress={handleSave}
          type="BUTTON_SAVE"
          isGradient={true}
          textTop="0"
          sz={15}
        />
        <CustomButton
          text={t("common:cancel")}
          onPress={handleCancelChanges}
          type="BUTTON_CANCEL"
          textTop="0"
          sz={15}
        />
      </View>

      <SafeAreaView style={common.divider} />
      <FlatList
        style={{ height: flatListHeight }}
        data={rows}
        refreshing={isLoading}
        ref={ref}
        contentContainerStyle={{ padding: 20 }}
        showsVerticalScrollIndicator={true}
        renderItem={renderItem}
        keyExtractor={(item, index) => (item.id !== undefined ? item.id : index).toString()}
        ListHeaderComponent={
          <SafeAreaView style={common.listTitle}>
            < View style={[styles.headerRow, styles.tableRow]}>
              <CustomLabel value={t("common:salaryComponent")} type="SUBTITLE" style={styles.cell} />
              <CustomLabel value={t("common:current")} type="SUBTITLE" style={styles.cell} />
              <CustomLabel value={t("common:previous")} type="SUBTITLE" style={styles.cell} />
              <CustomLabel value={t("common:difference")} type="SUBTITLE" style={styles.cell} />
              <CustomLabel value={t("common:percentageDifference")} type="SUBTITLE" style={styles.cell} />
            </View>
          </SafeAreaView>
        }
        ListEmptyComponent={
          <ActivityIndicator animating={true} color={colors.blue} size="large" />
        }
      />
    </SafeAreaView>
  );
};

const makeStyles = (colors, width) =>
  StyleSheet.create({
    mainView: {
      flex: 1,
      padingTop: 5,
      paddingHorizontal: 5
    },
    container: {
      flex: 1,
    },
    title: {
      fontSize: 18,
      fontWeight: "600",
      marginBottom: 3,
      marginLeft: 10,
    },
    label: {
      fontWeight: "500",
      marginBottom: 4,
    },
    input: {
      borderWidth: 1,
      borderColor: "#0000",
      borderRadius: 4,
      padding: 8,
      backgroundColor: "#fafafa",
    },
    tableRow: {
      flexDirection: "row",
      alignItems: "center",
      borderColor: "#eee",
      marginVertical: 5,
      // width: "100%",
    },
    headerRow: {
      marginTop: 3,
    },
    cell: {
      flex: 1,
      padding: 8,
    },
    buttons: {
      flexDirection: "row",
      justifyContent: "flex-end",
      alignSelf: "flex-end",
      marginBottom: 15,
      marginRight: 15,
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
    },
  });

export default SalaryReviewScreen;
